import { Box } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, INCOME_MODE_HELP, INCOME_MODE_LABELS } from '../../../library/dashboardDisplay';
import { IClinicTotals, IncomeMode, incomeOf } from '../../../library/models/clinicDashboard';
import ResourcesIds from '../../../resources/ResourcesIds';
import { KpiTile } from '../../dashboard/KpiTile';
import { kpiGridStyles } from '../../dashboard/kpiTile.styles';

interface KpiRowProps {
    totals: IClinicTotals;
    mode: IncomeMode;
}

/**
 * The clinic's headline figures.
 *
 * Only the first tile answers to the income toggle. The other five are the same fact
 * whichever way income is counted, and must not so much as flicker when it moves --
 * a tile that changes for no reason is how a reader learns to distrust the ones that
 * changed for a reason.
 *
 * Active patients is deliberately not a tile of its own: it is new plus existing, both of
 * which are here already. It appears in the therapist table instead, where the two do not
 * add up to it and the difference is worth seeing.
 */
export const KpiRow = ({ totals, mode }: KpiRowProps): JSX.Element => {
    const { t: translate } = useTranslation();

    return (
        <Box sx={kpiGridStyles}>
            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_INCOME)}
                value={formatMoney(incomeOf(totals, mode), translate)}
                // The caption names the reading rather than repeating the label: on its
                // own, "Income" does not say which of the two answers this is.
                caption={translate(INCOME_MODE_LABELS[mode])}
                help={translate(INCOME_MODE_HELP[mode])}
                accented
                testId="kpi-income"
            />

            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_DEFERRED)}
                value={formatMoney(totals.deferred, translate)}
                help={translate(ResourcesIds.DASHBOARD_KPI_DEFERRED_HELP)}
                testId="kpi-deferred"
            />

            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_NEW_PATIENTS)}
                value={String(totals.newPatients)}
                testId="kpi-new-patients"
            />

            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_RETURNING_PATIENTS)}
                value={String(totals.existingPatients)}
                testId="kpi-existing-patients"
            />

            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_SESSIONS_HELD)}
                value={String(totals.sessionsHeld)}
                testId="kpi-sessions-held"
            />

            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_PURCHASES_SOLD)}
                value={String(totals.purchasesSold)}
                testId="kpi-purchases-sold"
            />
        </Box>
    );
};
